/**
 * dropoffPrecision — "how good is the drop-off pin?" in words and a tone.
 * ─────────────────────────────────────────────────────────────────────────────
 * The delivery fee is measured to shippingAddress.latLng, so every party that
 * looks at an order should see how that pin was set. Checkout stamps:
 *
 *   source        "gps"     the customer's live device fix
 *                 "places"  an address picked from search — the geocoder's
 *                           point for that place, not the exact gate
 *   gpsAccuracyM  for gps: the 68% radius of the fused fix, in metres
 *
 * Bands mirror the customer app's GPS_* constants: ≤15 m precise, ≤30 m good,
 * ≤100 m approximate (the customer accepted it explicitly), wider = rough.
 * Returns null when the order predates the fields, so callers can render
 * nothing without guarding.
 */
export const DROPOFF_TONES = {
  precise: { text: "#166534", bg: "#dcfce7", border: "#bbf7d0", dot: "#16a34a" },
  good: { text: "#166534", bg: "#dcfce7", border: "#bbf7d0", dot: "#16a34a" },
  approximate: { text: "#9a3412", bg: "#ffedd5", border: "#fed7aa", dot: "#ea580c" },
  rough: { text: "#991b1b", bg: "#fee2e2", border: "#fecaca", dot: "#dc2626" },
  places: { text: "#1e40af", bg: "#dbeafe", border: "#bfdbfe", dot: "#2563eb" },
};

export const describeDropoffPrecision = (shippingAddress) => {
  const source = shippingAddress?.source;
  if (source === "gps") {
    const m = Number(shippingAddress?.gpsAccuracyM);
    if (!Number.isFinite(m) || m <= 0) {
      return { tone: "approximate", label: "GPS pin", detail: "Accuracy not recorded", metres: null };
    }
    const r = Math.round(m);
    const tone = m <= 15 ? "precise" : m <= 30 ? "good" : m <= 100 ? "approximate" : "rough";
    const detail =
      tone === "precise" || tone === "good"
        ? "Live device fix — the pin is where the customer stood."
        : tone === "approximate"
        ? "Customer accepted an approximate fix — confirm the gate before the last turn."
        : "Rough fix — treat the pin as the neighbourhood and call ahead.";
    return { tone, label: `GPS pin · accurate to ±${r} m`, detail, metres: r };
  }
  if (source === "places") {
    return {
      tone: "places",
      label: "Address-search pin",
      detail: "Geocoder point for the address, not the exact gate — confirm with the customer.",
      metres: null,
    };
  }
  return null;
};
